import { action, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { type BillingMethod } from '@/features/billing/profile/BillingMethod'
import { type BillingProfileStore } from '@/features/billing/profile/BillingProfileStore'
import type { AlertErrorHandlerFactory } from '@/features/shell'

/**
 * View model for the dialog that confirms deleting a billing method.
 */
export class DeleteBillingMethodDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<boolean>({
    abort$: this.modal.close$,
  })

  /**
   * The billing method to delete.
   */
  @observable accessor method: BillingMethod | null = null

  constructor(
    private readonly _billingProfileStore: BillingProfileStore,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Asks the user to confirm deleting the billing method, which then gets
   * deleted.
   *
   * @returns whether the user confirmed.
   */
  async prompt(method: BillingMethod, signal?: AbortSignal) {
    this.modal.close()

    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this.method = method
      this.modal.show()
    })

    const response = await responsePromise
    this.modal.close()
    return response ?? false
  }

  /**
   * Whether the billing method is being deleted.
   */
  isDeleting(method: BillingMethod) {
    return this.delete.pending && this.method === method
  }

  /**
   * Closes the dialog without deleting.
   */
  @action.bound
  cancel() {
    this.modal.close()
  }

  /**
   * Deletes the billing method. The dialog closes right away, while the
   * list shows the deletion in progress.
   */
  delete = task.resolved(async () => {
    const method = this.method
    if (!method) {
      return
    }

    this._prompt.respond(true)
    this.modal.close()
    await this._billingProfileStore
      .deleteBillingMethod(method)
      .catch(
        this._createAlertErrorHandler('Deleting the payment method failed'),
      )
  })
}
